import { useRef, useState } from "react";
import { Link } from "@/i18n/LocaleLink";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { CONTACT, useContent } from "@/data/content";
import { MOTHER_SUPPORT } from "@/data/motherSupport";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/hooks/useSeo";

const BookButton = ({ label, testid }) => (
  <a
    href={CONTACT.mothersSessionUrl}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className="group inline-flex items-center gap-3 rounded-full bg-clay px-8 py-4 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust"
  >
    {label}
    <ArrowUpRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </a>
);

const Hero = ({ ms }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden grain" data-testid="ms-hero">
      <motion.div style={{ y: imgY }} className="absolute inset-0 -bottom-32">
        <img src="/images/irida-casual.jpg" alt="Two women in unhurried conversation over tea in warm natural light" className="h-full w-full object-cover scale-105" />
        <div className="absolute inset-0 bg-ink/40" />
        <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-ink/70 to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-end px-6 pb-20 pt-40 lg:px-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="mb-6 max-w-xl text-xs tracking-[0.3em] uppercase text-cream/85"
          data-testid="ms-hero-eyebrow"
        >
          {ms.hero.eyebrow}
        </motion.p>
        <div className="overflow-hidden">
          <motion.h1
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-cream text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-4xl"
            data-testid="ms-hero-title"
          >
            {ms.hero.title}
          </motion.h1>
        </div>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-7 max-w-2xl font-serif italic text-2xl leading-snug text-sand"
          data-testid="ms-hero-sub"
        >
          {ms.hero.sub}
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85 }}
          className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed text-cream"
          data-testid="ms-hero-intro"
        >
          {ms.hero.intro}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1 }}
          className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm tracking-wide text-sand"
          data-testid="ms-hero-facts"
        >
          {ms.hero.facts.map((f, i) => (
            <span key={f} className="flex items-center gap-5">
              {i > 0 && <span className="text-cream/40">·</span>}
              <span className={f.includes("€") ? "font-medium text-cream" : ""}>{f}</span>
            </span>
          ))}
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-9 flex flex-wrap items-center gap-6"
        >
          <BookButton label={ms.hero.primaryCta} testid="ms-hero-book" />
          <a href="#session" data-testid="ms-hero-more"
            className="group inline-flex items-center gap-2 text-sm tracking-wide text-cream/85 hover:text-cream transition-colors duration-300 border-b border-cream/40 pb-1">
            {ms.hero.secondaryCta}
            <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-ink/10" data-testid={`ms-faq-${index}`}>
      <button onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-center justify-between gap-6 py-5 text-left" data-testid={`ms-faq-toggle-${index}`}>
        <span className="font-serif text-lg sm:text-xl text-ink">{item.q}</span>
        <ChevronDown size={18} strokeWidth={1.5} className={`shrink-0 text-clay transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }} className="overflow-hidden">
            <p className="pb-6 pr-10 text-base leading-relaxed text-soft">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function MotherSupportPage() {
  const { lang } = useLang();
  const { offerings } = useContent();
  const ms = MOTHER_SUPPORT[lang] || MOTHER_SUPPORT.en;
  useSeo({ ...ms.seo, image: `/images/irida-casual.jpg` });
  const topicName = offerings.find((o) => o.slug === "one-to-one-mothers")?.name || "One-to-One Support for Mothers";
  const contactLink = `/contact?topic=${encodeURIComponent(topicName)}`;

  return (
    <main data-testid="mother-support-page">
      <Hero ms={ms} />

      <section className="bg-cream py-24 lg:py-36" data-testid="ms-recognition">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.2] tracking-tight text-ink">{ms.recognition.title}</h2>
            <p className="mt-8 font-serif text-2xl leading-snug text-clay italic">{ms.recognition.p1}</p>
            <p className="mt-7 text-base leading-relaxed text-soft">{ms.recognition.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ms.recognition.p3}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ms.recognition.p4}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ms.recognition.p5}</p>
            <p className="mt-10 font-serif italic text-xl sm:text-2xl leading-snug text-ink/80">{ms.recognition.closing}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-20 lg:py-28" data-testid="ms-because">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ms.because.title}</h2>
          </Reveal>
          <div className="mt-10 space-y-5">
            {ms.because.items.map((item, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <p className="text-base leading-relaxed text-soft border-l-2 border-clay/30 pl-6" data-testid={`ms-because-${i}`}>{item}</p>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="mt-10 font-serif italic text-lg leading-snug text-clay">{ms.because.note}</p>
          </Reveal>
        </div>
      </section>

      <section id="session" className="bg-cream py-24 lg:py-32" data-testid="ms-session">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl leading-snug tracking-tight text-ink">{ms.session.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft drop-cap">{ms.session.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ms.session.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ms.session.p3}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ms.session.p4}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ms.session.p5}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ms.session.p6}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{ms.session.p7}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-32" data-testid="ms-deeper">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl leading-snug tracking-tight text-ink">{ms.deeper.title}</h2>
            <p className="mt-7 font-serif text-2xl leading-snug text-clay italic">{ms.deeper.p1}</p>
            <p className="mt-6 text-base leading-relaxed text-soft">{ms.deeper.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ms.deeper.p3}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 border border-clay/30 bg-cream/60 p-10 text-center space-y-3" data-testid="ms-deeper-pairs">
              {ms.deeper.pairs.map((p, i) => (
                <p key={i} className="font-serif text-xl sm:text-2xl leading-snug text-ink/85">{p}</p>
              ))}
            </div>
            <p className="mt-10 text-base leading-relaxed text-soft">{ms.deeper.p5}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="ms-birth">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ms.birth.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{ms.birth.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ms.birth.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ms.birth.p3}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ms.birth.p4}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <blockquote className="mt-14 border border-clay/30 bg-parchment/60 p-10 lg:p-12 text-center" data-testid="ms-testimonial">
              <p className="font-serif italic text-5xl leading-none text-clay/40" aria-hidden>&ldquo;</p>
              <p className="mt-2 font-serif italic text-xl sm:text-2xl leading-relaxed text-ink/85">{ms.testimonial.text}</p>
              <footer className="mt-6 text-xs tracking-[0.25em] uppercase text-clay">— {ms.testimonial.name}</footer>
            </blockquote>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-16 lg:py-20" data-testid="ms-boundary">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">{ms.boundary.title}</h2>
            <p className="mt-6 text-base leading-relaxed text-soft">{ms.boundary.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ms.boundary.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ms.boundary.p3}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 lg:py-28" data-testid="ms-single">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ms.single.title}</h2>
            <p className="mt-7 font-serif text-2xl leading-snug text-clay italic">{ms.single.p1}</p>
            <p className="mt-6 text-base leading-relaxed text-soft">{ms.single.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ms.single.p3}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ms.single.p4}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-20 lg:py-28" data-testid="ms-baby">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ms.baby.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft">{ms.baby.p1}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ms.baby.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{ms.baby.p3}</p>
            <p className="mt-8 font-serif italic text-xl leading-snug text-ink/80">{ms.baby.closing}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-16 lg:py-24" data-testid="ms-where">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <Reveal>
            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">{ms.where.title}</h2>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
              <span className="font-serif text-2xl sm:text-3xl text-clay" data-testid="ms-where-online">{ms.where.options[0]}</span>
              <span className="text-sm text-soft italic">{ms.where.or}</span>
              <span className="font-serif text-2xl sm:text-3xl text-clay" data-testid="ms-where-person">{ms.where.options[1]}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-20 lg:py-28" data-testid="ms-irida">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4">
            <div className="relative">
              <div className="absolute -inset-4 rounded-t-[10rem] border border-clay/30" aria-hidden />
              <div className="overflow-hidden rounded-t-[10rem]">
                <img loading="lazy" src="/irida.jpg" alt="Irida Hysenbelli, founder of Women's Den" className="aspect-[3/4] w-full object-cover" data-testid="ms-irida-photo" />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7 lg:col-start-6">
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{ms.irida.heading}</h2>
            <p className="mt-6 text-base leading-relaxed text-soft drop-cap">{ms.irida.body}</p>
            <Link to="/about" data-testid="ms-irida-link"
              className="group mt-8 inline-flex items-center gap-3 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust">
              {ms.irida.link}
              <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-20 lg:py-24" data-testid="ms-crosslink">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <div className="border border-ink/10 bg-parchment/60 p-10 lg:p-14 text-center">
              <h2 className="font-serif text-2xl sm:text-3xl tracking-tight text-ink">{ms.crossLink.title}</h2>
              <p className="mt-5 text-sm leading-relaxed text-soft max-w-xl mx-auto">{ms.crossLink.body}</p>
              <Link to="/offerings/mums-babies-circle" data-testid="ms-crosslink-cta"
                className="group mt-7 inline-flex items-center gap-3 border-b border-clay pb-1 text-sm tracking-wide text-clay transition-colors duration-300 hover:border-rust hover:text-rust">
                {ms.crossLink.cta}
                <ArrowUpRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-16 lg:py-24" data-testid="ms-faq">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink mb-6">{ms.faq.heading}</h2>
          </Reveal>
          <Reveal>
            <div>
              {ms.faq.items.map((item, i) => <FaqItem key={i} item={item} index={i} />)}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-24 lg:py-32 grain relative" data-testid="ms-final">
        <div className="mx-auto max-w-3xl px-6 text-center relative z-10">
          <Reveal>
            <p className="font-serif italic text-2xl sm:text-3xl leading-snug text-sand max-w-2xl mx-auto" data-testid="ms-final-pull">{ms.final.pull}</p>
            <h2 className="mt-12 font-serif text-3xl sm:text-4xl tracking-tight text-cream">{ms.final.title}</h2>
            <div className="mt-8 space-y-2" data-testid="ms-final-rows">
              {ms.final.rows.map(([k, v]) => (
                <p key={k} className="text-sm text-cream/80">
                  <span className="text-sand/70 tracking-wide">{k}:</span> {v}
                </p>
              ))}
            </div>
            <div className="mt-10">
              <BookButton label={ms.final.cta} testid="ms-final-book" />
            </div>
            <Link to={contactLink} data-testid="ms-final-contact"
              className="group mt-8 inline-flex items-center gap-2 text-sm text-sand hover:text-cream transition-colors duration-300">
              {ms.final.secondary}
              <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
